import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { NavigatingButton } from '@sorare/core/src/atoms/buttons/NavigatingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { SwitchIcon } from '@sorare/core/src/atoms/icons/SwitchIcon';
import { iconUsers } from '@sorare/core/src/atoms/icons/system/iconUsers.ts';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  HeadlineXS,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS,
  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS,
  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS_CONTENDER,
} from '@sorare/core/src/constants/__generated__/routes';
import { Warning } from '@sorare/core/src/contexts/intl/Warning';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { FullLeaderboard } from '#src/components/leaderboard/PrizePoolWidget/FullLeaderboard/index.tsx';
import type { Props as RankRewardsProps } from '#src/components/leaderboard/RankRewards/index.tsx';
import { RankRewards } from '#src/components/leaderboard/RankRewards/index.tsx';
import { ScoreRewards } from '#src/components/leaderboard/ScoreRewards/index.tsx';
import { TargetsCarousel } from '#src/components/leaderboard/TargetsCarousel/index.tsx';
import { ThresholdProgressBar } from '#src/components/leaderboard/ThresholdProgressBar/index.tsx';
import { ThresholdRewards } from '#src/components/leaderboard/ThresholdRewards/index.tsx';
import { isFixtureOpened } from '#src/lib/so5.ts';

import type {
  DefaultPrizePool_so5Leaderboard,
  DefaultPrizePool_so5LeaderboardContender,
} from './__generated__/index.graphql.ts';

export const messages = defineMessages({
  leaderboardHasNoRewards: {
    defaultMessage: 'This leaderboard has no rewards',
  },
});

const StyledButton = styled(Button)`
  align-self: flex-end;
`;

export type Props = {
  hideTitle?: boolean;
  so5Leaderboard: DefaultPrizePool_so5Leaderboard;
  so5LeaderboardContender?: DefaultPrizePool_so5LeaderboardContender;
} & Pick<RankRewardsProps, 'onClickRanking'>;

export const DefaultPrizePool = ({
  hideTitle,
  so5Leaderboard,
  so5LeaderboardContender,
  onClickRanking,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  const isCumulativeLeaderboard =
    !!so5Leaderboard.universalSo5UserGroups?.length;
  const isUpcoming =
    isCumulativeLeaderboard &&
    so5Leaderboard.universalSo5UserGroups[0].startSo5Fixture
      ? isFixtureOpened(
          so5Leaderboard.universalSo5UserGroups[0].startSo5Fixture
        )
      : isFixtureOpened(so5Leaderboard.so5Fixture);

  const hasRankRewards = !!(so5Leaderboard.rewardsConfig.ranking || []).length;
  const hasScoreRewards = !!(so5Leaderboard.rewardsConfig.conditional || [])
    .length;
  const hasTargetRewards = !!(so5Leaderboard.rewardsConfig.targetable || [])
    .length;

  const hasMultipleTypeRewards =
    [hasRankRewards, hasScoreRewards, hasTargetRewards].filter(Boolean).length >
    1;

  const showFullLeaderboard =
    so5Leaderboard.so5LineupsCount < 21 &&
    so5Leaderboard.so5RankingsPaginated.nodes.length > 0 &&
    !isUpcoming;

  const [showAppearances, setShowAppearances] = useState<boolean>(true);

  const appearancesToggle = (showFullLeaderboard || hasRankRewards) &&
    !isUpcoming && (
      <StyledButton
        color="tertiary"
        size="small"
        onClick={() => {
          setShowAppearances(prev => !prev);
        }}
      >
        <FormattedMessage defaultMessage="Managers" />
        <SwitchIcon size="small" checked={showAppearances} />
      </StyledButton>
    );

  if (showFullLeaderboard) {
    return (
      <>
        {appearancesToggle}
        <FullLeaderboard
          slug={so5Leaderboard.slug}
          so5Lineup={so5LeaderboardContender?.so5Lineup || undefined}
          onClickRanking={onClickRanking}
          showAppearances={showAppearances}
        />
      </>
    );
  }

  return (
    <Vertical gap={2}>
      {appearancesToggle}
      {!hideTitle && (
        <SBHorizontal>
          <HeadlineXS as="h3">
            <FormattedMessage {...fantasy.leaderboard} />
          </HeadlineXS>
          <Horizontal>
            <FontAwesomeIcon icon={iconUsers} size="xs" />
            <LabelS as="p" bold>
              <FormattedNumber value={so5Leaderboard.so5LineupsCount} />
            </LabelS>
          </Horizontal>
        </SBHorizontal>
      )}
      <TargetsCarousel
        so5Leaderboard={so5Leaderboard}
        so5Lineup={so5LeaderboardContender?.so5Lineup || undefined}
      />
      {!!hasTargetRewards && (
        <>
          {!!hasMultipleTypeRewards && (
            <BodyS bold>
              <FormattedMessage {...fantasy.rewardsPerTarget} />
            </BodyS>
          )}
          <ScoreRewards
            targetable
            defaultRarity={so5Leaderboard.mainRarityType}
            so5RewardConfigs={so5Leaderboard.rewardsConfig.targetable || []}
            so5League={so5Leaderboard.so5League}
            so5Ranking={so5LeaderboardContender?.so5Lineup?.so5Rankings[0]}
          />
        </>
      )}
      {!!hasScoreRewards && !hasTargetRewards && (
        <>
          {!!hasMultipleTypeRewards && (
            <BodyS bold>
              <FormattedMessage {...fantasy.rewardsPerThreshold} />
            </BodyS>
          )}
          <ThresholdRewards
            so5RewardConfigs={so5Leaderboard.rewardsConfig.conditional || []}
            defaultRarity={so5Leaderboard.mainRarityType}
            so5League={so5Leaderboard.so5League}
          />
          <ThresholdProgressBar
            so5Leaderboard={so5Leaderboard}
            so5Lineup={so5LeaderboardContender?.so5Lineup}
          />
        </>
      )}
      {!!hasRankRewards && (
        <>
          {!!hasMultipleTypeRewards && (
            <BodyS bold>
              <FormattedMessage {...fantasy.rewardsPerRank} />
            </BodyS>
          )}
          <RankRewards
            so5Leaderboard={so5Leaderboard}
            so5Ranking={so5LeaderboardContender?.so5Lineup?.so5Rankings[0]}
            onClickRanking={onClickRanking}
            showAppearances={showAppearances}
          />
        </>
      )}
      {!hasRankRewards && !hasScoreRewards && !hasTargetRewards && (
        <Warning title={messages.leaderboardHasNoRewards} variant="yellow" />
      )}
      {!isUpcoming && (
        <Portal id="contender-drawer-content-sticky-footer">
          <NavigatingButton
            color="tertiary"
            size="medium"
            to={generateSportContextPath(
              so5LeaderboardContender
                ? ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS_CONTENDER
                : ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS,
              {
                params: {
                  eventType: so5Leaderboard.so5Fixture.type.toLowerCase(),
                  leaderboard: so5Leaderboard.slug,
                  contender: so5LeaderboardContender?.slug || '',
                },
              }
            )}
          >
            <FormattedMessage {...fantasy.viewLeaderboard} />
          </NavigatingButton>
        </Portal>
      )}
      {!!isUpcoming && (
        <Link
          to={generateSportContextPath(
            ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS,
            {
              params: {
                eventType: so5Leaderboard.so5Fixture.type.toLowerCase(),
                fixture: so5Leaderboard.so5Fixture.slug,
              },
            }
          )}
        >
          <BodyM as="p" bold color="var(--c-blue-300)">
            <FormattedMessage {...fantasy.viewRewardsPool} />
          </BodyM>
        </Link>
      )}
    </Vertical>
  );
};

DefaultPrizePool.fragments = {
  so5Leaderboard: gql`
    fragment DefaultPrizePool_so5Leaderboard on So5Leaderboard {
      slug
      so5LineupsCount
      mainRarityType
      so5RankingsPaginated(pageSize: 1) {
        nodes {
          id
        }
      }
      so5League {
        slug
        ...ScoreRewards_so5League
        ...ThresholdRewards_so5League
      }
      so5Fixture {
        slug
        type
        aasmState
      }
      rewardsConfig {
        ranking {
          id
          ranks
        }
        conditional {
          id
          score
          ...ThresholdRewards_so5RewardConfig
        }
        targetable {
          id
          score
          ...ScoreRewards_so5RewardConfig
        }
      }
      universalSo5UserGroups {
        slug
        startSo5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
        }
      }
      ...RankRewards_so5Leaderboard
      ...ThresholdProgressBar_so5Leaderboard
      ...TargetsCarousel_so5Leaderboard
    }
    ${ThresholdRewards.fragments.so5League}
    ${ThresholdRewards.fragments.so5RewardConfig}
    ${isFixtureOpened.fragments.so5Fixture}
    ${RankRewards.fragments.so5Leaderboard}
    ${ScoreRewards.fragments.so5League}
    ${ScoreRewards.fragments.so5RewardConfig}
    ${ThresholdProgressBar.fragments.so5Leaderboard}
    ${TargetsCarousel.fragments.so5Leaderboard}
  ` as TypedDocumentNode<DefaultPrizePool_so5Leaderboard>,
  so5LeaderboardContender: gql`
    fragment DefaultPrizePool_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        so5Rankings {
          id
          ...RankRewards_so5Ranking
          ...ScoreRewards_so5Ranking
        }
        ...ThresholdProgressBar_so5Lineup
        ...TargetsCarousel_so5Lineup
      }
    }
    ${RankRewards.fragments.so5Ranking}
    ${ScoreRewards.fragments.so5Ranking}
    ${ThresholdProgressBar.fragments.so5Lineup}
    ${TargetsCarousel.fragments.so5Lineup}
  ` as TypedDocumentNode<DefaultPrizePool_so5LeaderboardContender>,
};
